import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://uaooghxtslvyaizttavi.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVhb29naHh0c2x2eWFpenR0YXZpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUwOTc4MDUsImV4cCI6MjA3MDY3MzgwNX0.4zly1eOPShhZcVPqQzB360T0zGZU6FejRdIyvGVy2ds'

// Enhanced Supabase client configuration with proper auth settings
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Enable automatic token refresh
    autoRefreshToken: true,
    // Persist session in localStorage
    persistSession: true,
    // Detect session in URL (for OAuth flows)
    detectSessionInUrl: true,
    // Storage key for session data
    storageKey: 'cybergov-auth-token',
    // Enhanced cross-browser storage implementation with fallbacks
    storage: {
      getItem: (key) => {
        try {
          // Primary: localStorage
          if (typeof localStorage !== 'undefined' && localStorage.getItem) {
            const item = localStorage.getItem(key)
            if (item !== null) {
              // Validate JSON structure
              try {
                JSON.parse(item)
                return item
              } catch (parseError) {
                console.warn('Invalid JSON in localStorage, removing:', key)
                localStorage.removeItem(key)
                return null
              }
            }
          }
          
          // Fallback: sessionStorage
          if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem) {
            return sessionStorage.getItem(key)
          }
          
          // Fallback: memory storage
          if (window._cybergovMemoryStorage && window._cybergovMemoryStorage[key]) {
            return window._cybergovMemoryStorage[key]
          }
          
          return null
        } catch (error) {
          console.warn('Failed to get item from storage:', error)
          return null
        }
      },
      setItem: (key, value) => {
        try {
          // Validate value is valid JSON
          if (typeof value === 'string') {
            try {
              JSON.parse(value)
            } catch (parseError) {
              console.warn('Invalid JSON value, not storing:', key)
              return
            }
          }
          
          // Primary: localStorage
          if (typeof localStorage !== 'undefined' && localStorage.setItem) {
            try {
              localStorage.setItem(key, value)
              return
            } catch (localStorageError) {
              console.warn('localStorage failed, trying sessionStorage:', localStorageError)
            }
          }
          
          // Fallback: sessionStorage
          if (typeof sessionStorage !== 'undefined' && sessionStorage.setItem) {
            try {
              sessionStorage.setItem(key, value)
              return
            } catch (sessionStorageError) {
              console.warn('sessionStorage failed, using memory storage:', sessionStorageError)
            }
          }
          
          // Fallback: memory storage
          if (!window._cybergovMemoryStorage) {
            window._cybergovMemoryStorage = {}
          }
          window._cybergovMemoryStorage[key] = value
          
        } catch (error) {
          console.warn('Failed to set item in storage:', error)
        }
      },
      removeItem: (key) => {
        try {
          // Remove from all storage types
          if (typeof localStorage !== 'undefined' && localStorage.removeItem) {
            localStorage.removeItem(key)
          }
          if (typeof sessionStorage !== 'undefined' && sessionStorage.removeItem) {
            sessionStorage.removeItem(key)
          }
          if (window._cybergovMemoryStorage && window._cybergovMemoryStorage[key]) {
            delete window._cybergovMemoryStorage[key]
          }
        } catch (error) {
          console.warn('Failed to remove item from storage:', error)
        }
      }
    }
  },
  // Global configuration
  global: {
    headers: {
      'X-Client-Info': 'cybergov-web-app'
    }
  },
  // Real-time configuration
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Database table names
export const TABLES = {
  USERS: 'users',
  ASSESSMENTS: 'assessments',
  ASSESSMENT_RESPONSES: 'assessment_responses',
  TASKS: 'tasks',
  INCIDENTS: 'incidents',
  POLICIES: 'policies',
  LEARNING_MATERIALS: 'learning_materials',
  DOCUMENTS: 'documents',
  AGENCY_RISK_ASSIGNMENTS: 'agency_risk_assignments'
}

// Storage bucket names
export const STORAGE_BUCKETS = {
  POLICY_DOCUMENTS: 'policy-documents'
}

// Helper functions for common operations
export const dbHelpers = {
  // Normalize category strings to canonical dashboard keys
  normalizeCategory(cat) {
    if (!cat) return undefined
    const key = String(cat).trim()
    // Already-normalized keys
    const direct = ['dataProtection','accessControl','networkSecurity','incidentResponse','training','compliance','physicalSecurity','businessContinuity','general']
    if (direct.includes(key)) return key
    // Dash-case variants
    if (key === 'data-protection') return 'dataProtection'
    if (key === 'access-control') return 'accessControl'
    if (key === 'network-security') return 'networkSecurity'
    if (key === 'incident-response') return 'incidentResponse'
    // Database category names
    if (key === 'Network Security') return 'networkSecurity'
    if (key === 'Data Protection') return 'dataProtection'
    if (key === 'Access Control') return 'accessControl'
    if (key === 'Incident Response') return 'incidentResponse'
    if (key === 'Security Awareness') return 'training'
    if (key === 'Compliance & Governance') return 'compliance'
    if (key === 'Physical Security') return 'physicalSecurity'
    if (key === 'Business Continuity') return 'businessContinuity'
    // Keyword heuristics
    const lower = key.toLowerCase()
    if (lower.includes('network')) return 'networkSecurity'
    if (lower.includes('data') || lower.includes('backup')) return 'dataProtection'
    if (lower.includes('access') || lower.includes('password')) return 'accessControl'
    if (lower.includes('incident') || lower.includes('response')) return 'incidentResponse'
    if (lower.includes('awareness') || lower.includes('training')) return 'training'
    if (lower.includes('compliance') || lower.includes('governance')) return 'compliance'
    return undefined
  },
  // User operations
  async createUserProfile(userId, profileData) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .insert({
        id: userId,
        ...profileData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async getUserProfile(userId) {
    const run = async () => supabase
      .from(TABLES.USERS)
      .select('*')
      .eq('id', userId)
      .single()
    const { data, error } = await executeWithAuthRetry(run)
    if (error) throw error
    return data
  },

  async updateUserProfile(userId, updates) {
    const run = async () => supabase
      .from(TABLES.USERS)
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single()
    const { data, error } = await executeWithAuthRetry(run)
    if (error) throw error
    return data
  },

  // Assessment operations
  async createAssessment(userId, assessmentData) {
    const { data, error } = await supabase
      .from(TABLES.ASSESSMENTS)
      .insert({
        user_id: userId,
        ...assessmentData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async getUserAssessments(userId) {
    const run = async () => supabase
      .from(TABLES.ASSESSMENTS)
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    const { data, error } = await executeWithAuthRetry(run)
    if (error) throw error
    return data
  },

  // Task operations
  async createTask(userId, taskData) {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .insert({
        user_id: userId,
        // Set agency_id from provided taskData or derive from users table via RPC/fallback
        agency_id: taskData.agency_id || null,
        ...taskData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async getUserTasks(userId) {
    const run = async () => supabase
      .from(TABLES.TASKS)
      .select('*')
      // Show tasks created by user or assigned to user
      .or(`user_id.eq.${userId},assigned_to.eq.${userId}`)
      // Also scope by the user's agency when available
      .in('agency_id', [
        // NULL agency_id tasks allowed for legacy/backfill
        null,
        // User's agency_id: fetch via subquery supported by PostgREST using eq on view when available
      ])
      .order('due_date', { ascending: true })
    const { data, error } = await executeWithAuthRetry(run)
    if (error) throw error
    return data
  },

  async updateTask(taskId, updates) {
    const run = async () => supabase
      .from(TABLES.TASKS)
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', taskId)
      .select()
      .single()
    const { data, error } = await executeWithAuthRetry(run)
    if (error) throw error
    return data
  },

  // Bulk task creation for compliance assignments
  async createTasksBulk(userId, tasks, overrides = {}) {
    // Map string priorities to numeric enums expected by DB (1=low, 2=medium, 3=high, 4=critical)
    const priorityMap = { low: 1, medium: 2, high: 3, critical: 4 }
    const payload = (tasks || []).map(t => {
      const p = t.priority
      const priorityNumeric = typeof p === 'number'
        ? p
        : priorityMap[String(p || 'high').toLowerCase()] || 2

      const row = {
        // Insert under the current user (assigner) to satisfy INSERT RLS
        user_id: userId,
        // Link task to the originating assessment to avoid cross-assessment overrides
        assessment_id: overrides.assessment_id || t.assessment_id || null,
        // Keep foreign key to assessment question when available
        question_id: t.question_id || null,
        // Track assignment metadata so assignees can view via assigned_to policies
        assigned_by: userId,
        assigned_to: overrides.assigned_to || t.assigned_to || null,
        title: t.title,
        description: t.description,
        category: dbHelpers.normalizeCategory(t.category) || null,
        priority: priorityNumeric,
        status: t.status || 'pending',
        due_date: overrides.due_date || t.due_date || new Date(Date.now() + 1000 * 60 * 60 * 24 * 14).toISOString(),
        created_at: overrides.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      // Include agency_id only when explicitly provided and non-null
      const agencyId = overrides.agency_id || t.agency_id || null
      if (agencyId != null && agencyId !== '') {
        row.agency_id = agencyId
      }
      return row
    })
    if (!payload.length) return []

    const runInsert = async (rows) => supabase
      .from(TABLES.TASKS)
      .insert(rows)
      .select()

    // First attempt: include agency_id when present
    const { data, error } = await executeWithAuthRetry(() => runInsert(payload))
    if (!error) return data

    const msg = String(error?.message || '').toLowerCase()
    const qidMissing =
      msg.includes("could not find the 'question_id' column") ||
      (msg.includes('could not find') && msg.includes('question_id') && msg.includes('schema cache')) ||
      msg.includes('column question_id does not exist')
    const columnMissing =
      msg.includes("could not find the 'agency_id' column") ||
      (msg.includes('could not find') && msg.includes('agency_id') && msg.includes('schema cache')) ||
      msg.includes('column agency_id does not exist')

    const rlsViolation =
      msg.includes('row-level security') ||
      msg.includes('rls') ||
      msg.includes('not authorized') ||
      msg.includes('permission denied')

    // Retry without question_id when the column is missing
    if (qidMissing) {
      const withoutQid = payload.map(({ question_id, ...rest }) => rest)
      const { data: dataQ, error: errQ } = await executeWithAuthRetry(() => runInsert(withoutQid))
      if (!errQ) return dataQ
      // fall through to other checks if still failing
    }

    if (columnMissing || rlsViolation) {
      // Retry without agency_id to avoid schema-cache or RLS issues
      const withoutAgency = payload.map(({ agency_id, ...rest }) => rest)
      const { data: data2, error: error2 } = await executeWithAuthRetry(() => runInsert(withoutAgency))
      if (!error2) return data2
      throw error2
    }

    throw error
  },

  // Generate compliance tasks from an assessment object
  generateComplianceTasksFromAssessment(assessment) {
    const tasks = []
    const recs = Array.isArray(assessment?.recommendations) ? assessment.recommendations : []
    const agencyName = assessment?.agency?.name || assessment?.agency || null
    const assessmentTag = `AID:${assessment?.id || 'unknown'}`
    const agencyTag = agencyName ? `AG:${agencyName}` : null

    const prefix = agencyTag ? `[${agencyTag}] [${assessmentTag}]` : `[${assessmentTag}]`

    // Build maps for questions and recommendations
    const questionTitles = assessment?.question_titles || {}
    const questionMap = assessment?.questions || {}
    const responses = assessment?.responses || {}

    // Identify failing questions from responses (no/low/false)
    const failingQids = new Set()
    Object.entries(responses).forEach(([qid, value]) => {
      const v = typeof value === 'object' ? (value?.score ?? value?.value) : value
      const isFail = (typeof v === 'number' && v < 0.5) || (typeof v === 'string' && v.toLowerCase() === 'no') || v === false
      if (isFail) failingQids.add(String(qid))
    })

    // Index recommendations by question id if available
    const recByQid = new Map()
    for (const r of recs) {
      if (!r || !r.title) continue
      const qid = r.id || r.question_id || null
      if (qid) {
        recByQid.set(String(qid), r)
        // Also mark as failing to avoid duplicates when responses contain same qid
        failingQids.add(String(qid))
      }
    }

    // Create one task per unique failing question id
    for (const qid of failingQids) {
      const rec = recByQid.get(qid)
      const friendly = questionTitles[qid] || questionMap[qid]?.title || null
      const qCategory = (rec?.category) || (questionMap[qid]?.category) || null

      // Use exact question text as title (without "Improve" prefix)
      const baseTitle = friendly || rec?.title || null
      if (!baseTitle) continue

      tasks.push({
        title: `${prefix} [Compliance] ${baseTitle}`,
        description: rec?.description || 'Remediate missing/insufficient implementation identified in the assessment.',
        category: dbHelpers.normalizeCategory(qCategory) || (friendly ? dbHelpers.normalizeCategory(friendly) : undefined),
        priority: (rec?.priority || 'medium').toLowerCase(),
        question_id: String(qid)
      })
    }

    // Fallback: if no question ids were detected, dedupe by normalized title
    if (tasks.length === 0 && recs.length > 0) {
      const seen = new Set()
      for (const r of recs) {
        if (!r || !r.title) continue
        const norm = r.title.trim().toLowerCase()
        if (seen.has(norm)) continue
        seen.add(norm)
        tasks.push({
          title: `${prefix} [Compliance] ${r.title}`,
          description: r.description || 'Implement the recommended control to address identified gaps.',
          category: dbHelpers.normalizeCategory(r.category) || undefined,
          priority: (r.priority || 'high').toLowerCase()
        })
      }
    }

    return tasks
  },

  // Backfill categories for existing tasks based on latest assessment-derived compliance tasks
  async backfillTaskCategoriesFromAssessment(assessment) {
    try {
      if (!assessment) return 0
      const preview = dbHelpers.generateComplianceTasksFromAssessment(assessment) || []
      const titles = preview.map(t => t.title).filter(Boolean)
      if (titles.length === 0) return 0
      const titleToCategory = preview.reduce((acc, t) => {
        const norm = dbHelpers.normalizeCategory(t.category)
        if (t.title && norm) acc[t.title] = norm
        return acc
      }, {})
      const userId = assessment.user_id || assessment.userId
      if (!userId) return 0
      const runFetch = async () => supabase
        .from(TABLES.TASKS)
        .select('id,title,category,assigned_to,user_id')
        .or(`assigned_to.eq.${userId},user_id.eq.${userId}`)
        .in('title', titles)
      const { data: existing, error } = await executeWithAuthRetry(runFetch)
      if (error) throw error
      let updatedCount = 0
      for (const t of existing || []) {
        const desired = titleToCategory[t.title]
        if (desired && (!t.category || dbHelpers.normalizeCategory(t.category) !== desired)) {
          const runUpdate = async () => supabase
            .from(TABLES.TASKS)
            .update({ category: desired, updated_at: new Date().toISOString() })
            .eq('id', t.id)
          const { error: updErr } = await executeWithAuthRetry(runUpdate)
          if (!updErr) updatedCount++
        }
      }
      return updatedCount
    } catch (e) {
      console.warn('Backfill categories failed:', e)
      return 0
    }
  },

  // Assign compliance tasks derived from assessment to a user
  async assignComplianceTasksFromAssessment(userId, assessment, overrides = {}) {
    const tasksBase = dbHelpers.generateComplianceTasksFromAssessment(assessment)
    // Default newly assigned tasks to pending status, allow override (e.g., 'scheduled')
    const desiredStatus = overrides.status || 'pending'
    const tasks = (tasksBase || []).map(t => ({ ...t, status: desiredStatus }))
    // Ensure tasks are assigned to the assessment owner while being created by the current user
    const assignedTo = assessment?.user_id || assessment?.userId || null

    // Try to auto-populate agency_id from assessment owner if not provided
    let agencyId = overrides.agency_id || null
    try {
      if (!agencyId && assignedTo) {
        const runOwner = async () => supabase
          .from(TABLES.USERS)
          .select('id, agency_id')
          .eq('id', assignedTo)
          .single()
        const { data: ownerProfile } = await executeWithAuthRetry(runOwner)
        agencyId = ownerProfile?.agency_id || null
      }
    } catch (e) {
      console.warn('Failed to auto-populate agency_id for compliance assignment:', e)
    }

    const mergedOverrides = { ...overrides, agency_id: agencyId, assigned_to: assignedTo, assessment_id: assessment?.id }
    return await dbHelpers.createTasksBulk(userId, tasks, mergedOverrides)
  },

  // Mark task completed and update latest assessment risk for that user
  async completeTaskAndUpdateLatestAssessmentRisk(taskId) {
    // Fetch task to get user
    const runGetTask = async () => supabase
      .from(TABLES.TASKS)
      .select('*')
      .eq('id', taskId)
      .single()
    const { data: task, error: taskErr } = await executeWithAuthRetry(runGetTask)
    if (taskErr) throw taskErr
    if (!task) throw new Error('Task not found')

    // Mark completed
    await dbHelpers.updateTask(taskId, { status: 'completed' })

    // Get latest completed assessment for the user
    const runGetAssess = async () => supabase
      .from(TABLES.ASSESSMENTS)
      .select('*')
      .eq('user_id', task.user_id)
      .eq('status', 'completed')
      .order('completed_at', { ascending: false })
      .limit(1)
      .single()
    const { data: latestAssessment, error: assessErr } = await executeWithAuthRetry(runGetAssess)
    if (assessErr) throw assessErr
    if (!latestAssessment) return null

    // Count completed compliance tasks since the assessment was created/completed
    const since = latestAssessment.completed_at || latestAssessment.created_at
    const runCompletedTasks = async () => supabase
      .from(TABLES.TASKS)
      .select('id,title,updated_at')
      .eq('user_id', task.user_id)
      .eq('status', 'completed')
      .gte('updated_at', since)
    const { data: completed, error: compErr } = await executeWithAuthRetry(runCompletedTasks)
    if (compErr) throw compErr

    const complianceCompleted = (completed || []).filter(t => (t.title || '').toLowerCase().includes('[compliance]')).length

    // Simple scoring model: +2 per compliance task, max 100
    const baseScore = Number(latestAssessment.score || 0)
    const newScore = Math.min(100, baseScore + complianceCompleted * 2)
    const newRisk = newScore >= 85 ? 'low' : newScore >= 60 ? 'medium' : 'high'

    // Update assessment score and risk
    const runUpdateAssess = async () => supabase
      .from(TABLES.ASSESSMENTS)
      .update({
        score: newScore,
        risk_level: newRisk,
        updated_at: new Date().toISOString()
      })
      .eq('id', latestAssessment.id)
      .select()
      .single()
    const { data: updated, error: updErr } = await executeWithAuthRetry(runUpdateAssess)
    if (updErr) throw updErr
    return updated
  },

  // Document storage operations
  async uploadDocument(file, metadata, userId) {
    try {
      // Create unique file path with user ID folder
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `${userId}/${fileName}`

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKETS.POLICY_DOCUMENTS)
        .upload(filePath, file)

      if (uploadError) throw uploadError

      // Save document metadata to database
      const { data: docData, error: docError } = await supabase
        .from(TABLES.DOCUMENTS)
        .insert({
          title: metadata.title,
          description: metadata.description,
          category: metadata.category,
          type: metadata.type,
          file_name: file.name,
          file_size: file.size,
          file_format: fileExt.toUpperCase(),
          storage_path: filePath,
          uploaded_by: userId,
          tags: metadata.tags || [],
          is_public: metadata.isPublic !== false
        })
        .select()
        .single()

      if (docError) {
        // Clean up uploaded file if database insert fails
        await supabase.storage
          .from(STORAGE_BUCKETS.POLICY_DOCUMENTS)
          .remove([filePath])
        throw docError
      }

      return { document: docData, storagePath: uploadData.path }
    } catch (error) {
      console.error('Document upload error:', error)
      throw error
    }
  },

  async getDocuments(filters = {}) {
    let query = supabase
      .from(TABLES.DOCUMENTS)
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })

    if (filters.category && filters.category !== 'all') {
      query = query.eq('category', filters.category)
    }

    if (filters.type && filters.type !== 'all') {
      query = query.eq('type', filters.type)
    }

    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
    }

    const { data, error } = await query
    if (error) throw error
    return data
  },

  async getDocumentUrl(storagePath) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.POLICY_DOCUMENTS)
      .createSignedUrl(storagePath, 3600) // 1 hour expiry

    if (error) throw error
    return data.signedUrl
  },

  async deleteDocument(documentId, userId) {
    // Get document details first
    const { data: doc, error: fetchError } = await supabase
      .from(TABLES.DOCUMENTS)
      .select('storage_path, uploaded_by')
      .eq('id', documentId)
      .eq('uploaded_by', userId)
      .single()

    if (fetchError) throw fetchError
    if (!doc) throw new Error('Document not found or unauthorized')

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKETS.POLICY_DOCUMENTS)
      .remove([doc.storage_path])

    if (storageError) throw storageError

    // Delete from database
    const { error: dbError } = await supabase
      .from(TABLES.DOCUMENTS)
      .delete()
      .eq('id', documentId)
      .eq('uploaded_by', userId)

    if (dbError) throw dbError
    return true
  },

  async incrementDocumentDownloads(documentId) {
    const { error } = await supabase.rpc('increment_document_downloads', {
      document_id: documentId
    })
    
    if (error) throw error
  }
}

// Auth-aware retry helper to keep data syncing after idle
export async function executeWithAuthRetry(runQuery) {
  try {
    const { data, error } = await runQuery()
    if (!error) return { data, error: null }
    if (isAuthExpiredError(error)) {
      await supabase.auth.refreshSession()
      const retry = await runQuery()
      return { data: retry.data, error: retry.error || null }
    }
    return { data, error }
  } catch (err) {
    if (isAuthExpiredError(err)) {
      await supabase.auth.refreshSession()
      const retry = await runQuery()
      return { data: retry.data, error: retry.error || null }
    }
    return { data: null, error: err }
  }
}

function isAuthExpiredError(error) {
  const msg = (error?.message || String(error || '')).toLowerCase()
  return (
    msg.includes('jwt expired') ||
    msg.includes('invalid grant') ||
    msg.includes('refresh_token_not_found') ||
    msg.includes('invalid refresh token') ||
    msg.includes('token expired')
  )
}

// Backfill missing assessment_id for compliance tasks based on a given assessment
export async function backfillAssessmentIdFromAssessment(assessment) {
  try {
    if (!assessment) return 0
    const userId = assessment.user_id || assessment.userId
    if (!userId) return 0
    const preview = dbHelpers.generateComplianceTasksFromAssessment(assessment) || []
    const titles = preview.map(t => t.title).filter(Boolean)
    if (titles.length === 0) return 0
    const runFetch = async () => supabase
      .from(TABLES.TASKS)
      .select('id,title,assessment_id,assigned_to,user_id')
      .or(`assigned_to.eq.${userId},user_id.eq.${userId}`)
      .is('assessment_id', null)
      .in('title', titles)
    const { data: missing, error } = await executeWithAuthRetry(runFetch)
    if (error) throw error
    const ids = (missing || []).map(t => t.id)
    if (ids.length === 0) return 0
    const runUpdate = async () => supabase
      .from(TABLES.TASKS)
      .update({ assessment_id: assessment.id, updated_at: new Date().toISOString() })
      .in('id', ids)
    const { error: updErr } = await executeWithAuthRetry(runUpdate)
    if (updErr) throw updErr
    return ids.length
  } catch (e) {
    console.warn('Backfill assessment_id failed:', e)
    return 0
  }
}
